import * as Sentry from "@sentry/react";
import { getEnv } from "./env.helpers";

/**
 * @module logger
 *
 * Provides a wrapper for console logging in dev environments.
 *
 * If we introduce client-side observability in production
 * then we can report from these functions.
 *
 * See https://github.com/vitejs/vite/discussions/7920#discussioncomment-4813594
 * for details on dropping console.log in builds.
 */

type Level = "log" | "warn" | "error";
type Primitive = "string" | "number" | "boolean" | "null" | "undefined";

const console = (
  level: Level,
  sentryLevel: Sentry.SeverityLevel,
  args: unknown[]
) => {
  if (getEnv() !== "prod") {
    globalThis.console[level](...args);
  } else {
    const message =
      args.map(
        (arg: unknown) =>
          (typeof arg === "object" ? JSON.stringify(arg) : arg) as Primitive
      ) ?? [];
    Sentry.captureMessage(
      `Logger ${level}: ${message.join(", ")}`,
      sentryLevel
    );
  }
};

/**
 * Debug logging only writes to the console. Is ignored in production.
 */
export const debug = (...args: unknown[]) => {
  if (getEnv() !== "prod") {
    globalThis.console.log(...args);
  }
};

export const log = (...args: unknown[]) => {
  console("log", "log", args);
};

export const warn = (...args: unknown[]) => {
  console("warn", "warning", args);
};

export const error = (...args: unknown[]) => {
  console("error", "error", args);
};

export default { log, warn, error };
